/**
 * How an uptime figure is rated and rendered.
 *
 * A pure module for the same reason `fleet-state.ts` is one: the predecessor
 * kept its equivalent inline in a 12,000-line HTML file, in two places that
 * disagreed, and a failed SD card rendered green in one and red in the other.
 * Logic that decides what colour a number is has to be somewhere a test can
 * reach it.
 *
 * Everything here was written after LOOKING at the page. The unit tests over
 * `src/uptime/summary.ts` all passed while the screen was still drawing a
 * confident green 100% for a camera that had been silent for twelve days —
 * because the API was returning the silence correctly and the rendering was
 * throwing it away.
 */

export interface UptimeFigure {
  uptime: number | null;
  definite: number;
  total: number;
  expectedReadings: number | null;
  trailingSilenceMs: number;
}

export type Band = 'ok' | 'warning' | 'fault' | 'stale';

/**
 * Below this many definite readings, a ratio is arithmetic rather than
 * evidence. Three passes is "100%" and means nothing.
 *
 * A judgement, not a statistical claim: at thirty readings one failure moves
 * the figure to 96.7%, which is enough for it to respond to reality at all.
 */
export const RATEABLE_READINGS = 30;

/**
 * How much silence makes a figure untrustworthy: a tenth of the window.
 *
 * Relative to the window rather than to the check's cadence, deliberately. A
 * missed poll is normal. Losing a tenth of the period means we were not
 * watching, whatever the cadence was meant to be.
 */
export function silenceThresholdMs(windowMs: number): number {
  return windowMs / 10;
}

/**
 * 🔴 Silence and sample size both override the value.
 *
 * The ordering is the point. A figure is only allowed to be green when there
 * was something to measure AND we were there to measure it. Both overrides
 * were added after the page showed a camera with 2,591 consecutive passes and
 * twelve days of subsequent silence as a green 100.00%, with the only
 * contradiction eleven columns to the right.
 */
export function band(figure: UptimeFigure, silenceThreshold: number): Band {
  if (figure.uptime === null) return 'stale';
  if (figure.trailingSilenceMs > silenceThreshold) return 'stale';
  if (figure.definite < RATEABLE_READINGS) return 'stale';
  if (figure.uptime >= 0.99) return 'ok';
  if (figure.uptime >= 0.95) return 'warning';
  return 'fault';
}

/**
 * Proportion of the expected record actually held, 0..1. Null when the
 * cadence is unknown.
 *
 * Kept separate from sample size, which an earlier version conflated: 4,319
 * readings is a trustworthy ratio at mediocre coverage, while 3 readings is
 * untrustworthy at any coverage. Merging them put an identical badge on both,
 * and a warning that fires on every row is not a warning.
 */
export function coverage(figure: UptimeFigure): number | null {
  if (figure.expectedReadings === null || figure.expectedReadings === 0) return null;
  return Math.min(1, figure.total / figure.expectedReadings);
}

/**
 * `0.9993` becomes `"99.93%"`. Null becomes the words, never a number.
 *
 * Truncated, not rounded. `toFixed(2)` renders 99.995% as "100.00%", so a
 * device that was definitely down shows a clean sheet at exactly the precision
 * an SLA is argued over. Only a true 1 prints 100.00%.
 *
 * 🔴 Deliberately identical to `formatUptime` in `src/uptime/summary.ts`, and
 * `test/uptime_state.test.ts` asserts the two agree across a sweep of values.
 * They are separate builds and cannot share a module, so the guarantee has to
 * be a test rather than an import.
 */
export function formatUptime(uptime: number | null): string {
  if (uptime === null) return 'no answer';
  if (uptime >= 1) return '100.00%';
  return `${(Math.floor(uptime * 10_000) / 100).toFixed(2)}%`;
}

export function formatDuration(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  if (ms < 172_800_000) return `${Math.round(ms / 3_600_000)}h`;
  return `${Math.round(ms / 86_400_000)}d`;
}
